import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authentikConfig } from '../config/authentik.config';
import { generateCodeVerifier, generateCodeChallenge, generateState } from '../utils/pkce';
import { apiRequest, authenticatedFetch, refreshAccessToken } from '../config/api';

const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    // Add 30s buffer so we refresh slightly before actual expiry
    return payload.exp * 1000 < Date.now() + 30_000;
  } catch {
    return true;
  }
};

interface User {
  email: string;
  name: string;
  sub: string;
  preferred_username?: string;
  groups?: string[];
}

interface UserDetails {
  _id: string;
  name: string;
  email: string;
  userid: string;
  userRole: string;
  organization?: string;
  logo?: string;
  defaultFlightVisibility?: string;
}

interface AuthContextType {
  user: User | null;
  userDetails: UserDetails | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isStaff: boolean;
  login: () => Promise<void>;
  logout: () => void;
  handleCallback: (code: string, state: string) => Promise<void>;
  refreshUserDetails: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing session/token
    const checkAuth = async () => {
      try {
        let accessToken = localStorage.getItem('access_token');
        const refreshToken = localStorage.getItem('refresh_token');

        if (!accessToken && !refreshToken) {
          setIsLoading(false);
          return;
        }

        // Proactively refresh if the access token is missing or expired.
        // This avoids relying on a reactive 401 from the Authentik userinfo
        // endpoint (which may not behave like a standard backend 401).
        if (!accessToken || isTokenExpired(accessToken)) {
          if (!refreshToken) {
            setIsLoading(false);
            return;
          }
          accessToken = await refreshAccessToken();
          if (!accessToken) {
            setIsLoading(false);
            return;
          }
        }

        // Fetch user info — token is guaranteed fresh at this point
        const response = await authenticatedFetch(authentikConfig.userInfoEndpoint);

        if (response.ok) {
          const userInfo = await response.json();
          setUser(userInfo);

          // Fetch user details from our backend (apiRequest uses authenticatedFetch internally)
          const detailsResponse = await apiRequest('/users/me');
          if (detailsResponse.ok) {
            const details = await detailsResponse.json();
            setUserDetails(details);
          }
        } else {
          // Authentication failed, clear everything
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          setUser(null);
          setUserDetails(null);
        }
      } catch (error) {
        console.error('Auth check error:', error);
        // Clear invalid tokens
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        setUser(null);
        setUserDetails(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async () => {
    try {
      // Generate PKCE parameters
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      const state = generateState();

      // Store PKCE verifier and state
      sessionStorage.setItem('code_verifier', codeVerifier);
      sessionStorage.setItem('oauth_state', state);

      // Build authorization URL
      const params = new URLSearchParams({
        client_id: authentikConfig.clientId,
        redirect_uri: authentikConfig.redirectUri,
        response_type: authentikConfig.responseType,
        scope: authentikConfig.scopes.join(' '),
        state: state,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
      });

      const authUrl = `${authentikConfig.authorizationEndpoint}?${params.toString()}`;

      // Redirect to Authentik
      window.location.href = authUrl;
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  const handleCallback = async (code: string, state: string) => {
    try {
      // Verify state
      const storedState = sessionStorage.getItem('oauth_state');
      if (state !== storedState) {
        throw new Error('Invalid state parameter');
      }

      // Get code verifier
      const codeVerifier = sessionStorage.getItem('code_verifier');
      if (!codeVerifier) {
        throw new Error('Code verifier not found');
      }

      // Exchange code for tokens
      const tokenResponse = await fetch(authentikConfig.tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: authentikConfig.clientId,
          redirect_uri: authentikConfig.redirectUri,
          code: code,
          code_verifier: codeVerifier,
        }),
      });

      if (!tokenResponse.ok) {
        const errorText = await tokenResponse.text();
        throw new Error(`Token exchange failed: ${tokenResponse.status}`);
      }

      const tokens = await tokenResponse.json();

      // Store tokens
      localStorage.setItem('access_token', tokens.access_token);
      if (tokens.refresh_token) {
        localStorage.setItem('refresh_token', tokens.refresh_token);
      }

      // Fetch user info
      const userInfoResponse = await fetch(authentikConfig.userInfoEndpoint, {
        headers: {
          Authorization: `Bearer ${tokens.access_token}`,
        },
      });

      if (!userInfoResponse.ok) {
        throw new Error('Failed to fetch user info');
      }

      const userInfo = await userInfoResponse.json();
      setUser(userInfo);

      // Fetch user details from our backend
      const detailsResponse = await apiRequest('/users/me');
      if (detailsResponse.ok) {
        const details = await detailsResponse.json();
        setUserDetails(details);
      }

      // Clean up session storage
      sessionStorage.removeItem('code_verifier');
      sessionStorage.removeItem('oauth_state');
    } catch (error) {
      console.error('Callback handling error:', error);
      throw error;
    }
  };

  const refreshUserDetails = async () => {
    try {
      const detailsResponse = await apiRequest('/users/me');
      if (detailsResponse.ok) {
        const details = await detailsResponse.json();
        setUserDetails(details);
      }
    } catch (error) {
      console.error('Failed to refresh user details:', error);
    }
  };

  const logout = () => {
    // Clear tokens
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    setUserDetails(null);

    // Redirect to Authentik logout
    const logoutUrl = `${authentikConfig.endSessionEndpoint}?redirect_uri=${encodeURIComponent(window.location.origin)}`;
    window.location.href = logoutUrl;
  };

  const isStaff = user?.groups?.includes('dlr-staff') || false;

  return (
    <AuthContext.Provider
      value={{
        user,
        userDetails,
        isAuthenticated: !!user,
        isLoading,
        isStaff,
        login,
        logout,
        handleCallback,
        refreshUserDetails,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
